import { ActionBar, Box, Flex, IconButton, Portal, Text, useBreakpointValue } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CloseButton } from '@/components/ui/close-button';
import { ColDefLike, FilterChip, labelsFromColDefs, toNonDefaultFilterChips } from './toFilterChips';
import { GridFilterRef, useGridFilterActions } from './useGridFilterActions';

type GridFilterActionBarProps = {
    gridRef: { readonly current: GridFilterRef | null };
    filterModel: Record<string, unknown> | null | undefined;
    defaultFilterModel?: Record<string, unknown> | null;
    colDefs: ColDefLike[] | null | undefined;
    valueMaps?: Record<string, Record<string, string>>;
};

export function GridFilterActionBar({ gridRef, filterModel, defaultFilterModel, colDefs, valueMaps }: GridFilterActionBarProps) {
    const { removeFilter, clearFilters } = useGridFilterActions(gridRef, defaultFilterModel);
    const chips = toNonDefaultFilterChips(filterModel, defaultFilterModel, labelsFromColDefs(colDefs ?? []), valueMaps);
    const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;
    const countLabel = chips.length === 1 ? '1 filter' : `${chips.length} filters`;
    const hasDefaults = !!defaultFilterModel && Object.keys(defaultFilterModel).length > 0;
    const resetLabel = hasDefaults ? 'Reset' : 'Clear all';
    const resetAriaLabel = hasDefaults ? 'Reset to default filters' : 'Clear all filters';

    return (
        <ActionBar.Root open={chips.length > 0} autoFocus={false} closeOnEscape={false} closeOnInteractOutside={false}>
            <Portal>
                {isMobile ? (
                    <MobileBar
                        chips={chips}
                        countLabel={countLabel}
                        resetLabel={resetLabel}
                        onRemove={removeFilter}
                        onClear={clearFilters}
                    />
                ) : (
                    <DesktopBar
                        chips={chips}
                        countLabel={countLabel}
                        resetLabel={resetLabel}
                        resetAriaLabel={resetAriaLabel}
                        onRemove={removeFilter}
                        onClear={clearFilters}
                    />
                )}
            </Portal>
        </ActionBar.Root>
    );
}

type BarProps = {
    chips: FilterChip[];
    countLabel: string;
    resetLabel: string;
    onRemove: (colId: string) => void;
    onClear: () => void;
};

function DesktopBar({ chips, countLabel, resetLabel, resetAriaLabel, onRemove, onClear }: BarProps & { resetAriaLabel: string }) {
    return (
        <ActionBar.Positioner zIndex="popover" bottom="auto" top="calc(env(safe-area-inset-top, 0px) + 12px)">
            <ActionBar.Content
                flexWrap="nowrap"
                maxW="min(960px, calc(100vw - 32px))"
                py="1"
                overflowX="hidden"
                _open={{ animationName: 'slide-from-top, fade-in' }}
                _closed={{ animationName: 'slide-to-top, fade-out' }}>
                <ActionBar.SelectionTrigger flexShrink={0}>{countLabel}</ActionBar.SelectionTrigger>
                <ActionBar.Separator flexShrink={0} />
                <Flex gap="6px" wrap="nowrap" align="center" py="2px" minW={0} flex="1" overflowX="auto" scrollbar="hidden">
                    {chips.map((chip) => (
                        <FilterChipRow key={chip.colId} chip={chip} onRemove={onRemove} />
                    ))}
                </Flex>
                <Button variant="ghost" size="sm" color="fg.muted" flexShrink={0} onClick={onClear}>
                    {resetLabel}
                </Button>
                <ActionBar.CloseTrigger asChild>
                    <CloseButton size="sm" aria-label={resetAriaLabel} onClick={onClear} />
                </ActionBar.CloseTrigger>
            </ActionBar.Content>
        </ActionBar.Positioner>
    );
}

function MobileBar({ chips, countLabel, resetLabel, onRemove, onClear }: BarProps) {
    return (
        <ActionBar.Positioner zIndex="popover" pointerEvents="none" px="8px" top="auto" bottom="calc(env(safe-area-inset-bottom) + 12px)">
            <ActionBar.Content
                pointerEvents="auto"
                flexDirection="column"
                alignItems="stretch"
                w="100%"
                px="12px"
                py="10px"
                gap="8px"
                _open={{ animationName: 'slide-from-bottom, fade-in' }}
                _closed={{ animationName: 'slide-to-bottom, fade-out' }}>
                <Flex align="center" gap="8px">
                    <Text fontSize="sm" fontWeight="medium">
                        {countLabel}
                    </Text>
                    <Button type="button" variant="ghost" size="sm" color="fg.muted" ml="auto" minH="36px" onClick={onClear}>
                        {resetLabel}
                    </Button>
                </Flex>
                <Flex gap="6px" wrap="nowrap" overflowX="auto" scrollbar="hidden" css={{ WebkitOverflowScrolling: 'touch' }}>
                    {chips.map((chip) => (
                        <FilterChipRow key={chip.colId} chip={chip} onRemove={onRemove} large />
                    ))}
                </Flex>
            </ActionBar.Content>
        </ActionBar.Positioner>
    );
}

function FilterChipRow({ chip, onRemove, large }: { chip: FilterChip; onRemove: (colId: string) => void; large?: boolean }) {
    return (
        <Flex
            align="center"
            flexShrink={0}
            h={large ? '36px' : '28px'}
            maxW="280px"
            pl="10px"
            pr="2px"
            rounded="full"
            borderWidth="1px"
            borderColor={{ base: 'blackAlpha.200', _dark: 'whiteAlpha.200' }}
            bg={{ base: 'blackAlpha.50', _dark: 'whiteAlpha.100' }}
            title={`${chip.label}: ${chip.value}`}>
            <Text fontSize="11px" color="fg.muted" fontWeight="medium" textTransform="uppercase" flexShrink={0}>
                {chip.label}
            </Text>
            <Box w="1px" h="12px" mx="8px" bg={{ base: 'blackAlpha.200', _dark: 'whiteAlpha.300' }} />
            <Text fontSize="13px" fontWeight="medium" truncate minW={0}>
                {chip.value}
            </Text>
            <IconButton
                type="button"
                variant="ghost"
                size="xs"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => onRemove(chip.colId)}
                minW={large ? '32px' : '22px'}
                h={large ? '32px' : '22px'}
                ml="4px"
                rounded="full"
                color="fg.muted">
                <X size={12} />
            </IconButton>
        </Flex>
    );
}
